import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { LogoMark } from '@/components/generals/logo'
import { LiveDot } from '@/components/generals/live-dot'
import { ButtonLink } from '@/components/generals/button'

interface CtaLink {
    label: string
    href: string
}

interface CtaPanelProps {
    title?: string
    accent?: string
    primary?: CtaLink
    secondary?: CtaLink
    className?: string
}

const BARCODE = [3, 1, 1, 2, 4, 1, 2, 3, 1, 1, 3, 2, 1, 4, 2, 1, 1, 2, 3, 1, 2, 2, 1, 3, 1, 1, 4, 2, 1, 2, 3, 1, 1, 2, 2, 1, 3, 1, 2, 4]

const LABEL_FIELDS = [
    { label: "Service", value: "Managed Flow" },
    { label: "ETA", value: "11 days" },
    { label: "Handled by", value: "A real person" },
    { label: "Reply", value: "Within 1 day" }
]

/** A shipping label for the next departure: the visual half of the closing panel. */
const ShippingLabel: FC = (): ReactNode => {
    return (
        <div aria-hidden='true' className='relative mx-auto w-full max-w-[400px] rotate-[2deg] lg:rotate-[3deg]'>
            <div className='animate-float-soft'>
                <div className='relative rounded-[22px] bg-paper text-ink shadow-[0_40px_80px_-30px_rgba(8,10,90,0.7)]'>
                    <div className='p-5 sm:p-7'>
                        <div className='flex items-center justify-between'>
                            <span className='flex items-center gap-2'>
                                <LogoMark className='size-6' />
                                <span className='eyebrow text-ink'>
                                    Quayside
                                </span>
                            </span>

                            <span className='eyebrow text-stone'>
                                Label 0042
                            </span>
                        </div>

                        <div className='mt-6 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-2 sm:mt-7 sm:gap-3'>
                            <span>
                                <span className='eyebrow block text-stone'>
                                    From
                                </span>

                                <span className='mt-1.5 block font-heading text-[15px] font-semibold leading-tight tracking-[-0.03em] [overflow-wrap:break-word] sm:text-[19px]'>
                                    Four portals and a spreadsheet
                                </span>
                            </span>

                            <span className='mb-1 grid size-8 place-items-center rounded-full bg-ultramarine text-white sm:size-9'>
                                <ArrowRight className='size-4' />
                            </span>

                            <span className='text-right'>
                                <span className='eyebrow block text-stone'>
                                    To
                                </span>

                                <span className='mt-1.5 block font-heading text-[15px] font-semibold leading-tight tracking-[-0.03em] [overflow-wrap:break-word] sm:text-[19px]'>
                                    One live flow
                                </span>
                            </span>
                        </div>
                    </div>

                    <div className='relative'>
                        <span className='absolute -left-3 top-1/2 size-6 -translate-y-1/2 rounded-full bg-ultramarine' />
                        <span className='absolute -right-3 top-1/2 size-6 -translate-y-1/2 rounded-full bg-ultramarine' />
                        <div className='mx-5 border-t-2 border-dashed border-ink/15 sm:mx-6' />
                    </div>

                    <div className='p-5 sm:p-7'>
                        <dl className='grid grid-cols-2 gap-x-4 gap-y-5'>
                            {LABEL_FIELDS.map(field => (
                                <div key={field.label}>
                                    <dt className='eyebrow text-stone'>
                                        {field.label}
                                    </dt>

                                    <dd className='mt-1 text-[15px] font-semibold tracking-[-0.01em]'>
                                        {field.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>

                        <div className='mt-7 flex items-end justify-between gap-4'>
                            <div>
                                <div className='flex h-11 items-stretch'>
                                    {BARCODE.map((width, index) => (
                                        <span
                                            key={index}
                                            className={index % 2 === 0 ? 'bg-ink' : 'bg-transparent'}
                                            style={{ width: `${width * 1.6}px` }}
                                        />
                                    ))}
                                </div>

                                <span className='mt-1.5 block font-mono text-[10px] tracking-[0.3em] text-stone'>
                                    QS 2026 0042
                                </span>
                            </div>

                            <span className='eyebrow flex items-center gap-2 text-ink-soft'>
                                <LiveDot />
                                Ready
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

/** The closing call to action used at the end of every page: an ultramarine panel with a shipping label. */
export const CtaPanel: FC<CtaPanelProps> = ({
    title = "Ready to move everything?",
    accent = "Let's talk.",
    primary = { label: "Contact us", href: '/contact' },
    secondary = { label: "About Quayside", href: '/company' },
    className
}): ReactNode => {
    return (
        <section id='get-started' aria-labelledby='get-started-title' className={cn('section-y', className)}>
            <div className='shell'>
                <Reveal className='relative isolate overflow-hidden rounded-[32px] bg-ultramarine text-white sm:rounded-[44px]'>
                    <div aria-hidden='true' className='pointer-events-none absolute -right-40 -top-48 -z-10 size-[40rem] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.2),transparent)]' />
                    <div aria-hidden='true' className='pointer-events-none absolute -bottom-56 -left-32 -z-10 size-[36rem] rounded-full bg-[radial-gradient(closest-side,rgba(255,184,156,0.28),transparent)]' />

                    <div className='grid grid-cols-1 items-center gap-12 px-5 py-12 *:min-w-0 sm:gap-14 sm:px-12 sm:py-16 lg:grid-cols-12 lg:gap-10 lg:px-16 lg:py-20'>
                        <div className='lg:col-span-7'>
                            <p className='eyebrow flex items-center gap-2.5 text-white/60'>
                                <span aria-hidden='true' className='size-1.5 rounded-full bg-peach' />
                                Next departure
                            </p>

                            <h2 id='get-started-title' className='headline mt-5 text-[clamp(2.2rem,5.6vw,4.75rem)] [text-wrap:balance]'>
                                <span className='block'>
                                    {title}
                                </span>

                                <span className='block text-peach'>
                                    {accent}
                                </span>
                            </h2>

                            <p className='mt-6 max-w-lg text-[17px] leading-relaxed text-white/75 sm:text-lg'>
                                Tell us which lanes and systems are giving you trouble. A solutions engineer replies within one working day.
                            </p>

                            <div className='mt-9 flex flex-col items-start gap-5 sm:mt-10 sm:flex-row sm:items-center sm:gap-8'>
                                <ButtonLink href={primary.href} variant='light' size='lg' className='w-full sm:w-auto'>
                                    {primary.label}
                                    <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/btn:translate-x-0.5' />
                                </ButtonLink>

                                <Link
                                    href={secondary.href}
                                    className='group/secondary inline-flex items-center gap-1.5 text-[15px] font-medium text-white/85 transition-colors hover:text-white focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70'
                                >
                                    {secondary.label}
                                    <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/secondary:translate-x-1' />
                                </Link>
                            </div>
                        </div>

                        <div className='lg:col-span-5'>
                            <ShippingLabel />
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
